import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import TableChartIcon from "@mui/icons-material/TableChart";

import { withTranslation } from "react-i18next";

import fpDashboardService from "@plannel/services/fp/fp-dashboard-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import debug from "@plannel/utils/debug-util";

// old 위젯 공용 래퍼 FpBar.jsx의 막대 색. old는 CHART_DATA 엔트리 순서로 색을 배정했는데
// HashMap 직렬화 순서상 chartOutput이 선행이라 결과적으로 생산 주황 / 투입 대기 남색 —
// new는 시리즈별 고정 배정으로 동일 색을 보장한다(2026-08-11 브라우저 대조로 old와 일치 확인).
const CHART_FILL_PROD = "#f77e21";
const CHART_FILL_PRE_INPUT = "#2b5988";
// old RouteWip.jsx:78 — Total(합계) 행 배경 강조색.
const TOTAL_ROW_BACKGROUND = "#fdd7b5";

const CHART_OPTIONS = { minHeight: 180, margin: { left: 30, top: 30, bottom: 10, right: 30 } };

// 응답 LABEL("순번_공정그룹명_공정명") 배열을 2단 그룹 컬럼으로 변환(순서 보존).
// 그룹/자식 헤더는 데이터 값이라 i18n 대상이 아니다(GridUtils.getColumnDefs가 원문 유지).
const buildRouteColumnGroups = (labels, valueCellStyle) => {
  const groups = [];

  labels.forEach((label) => {
    const segments = String(label).split("_");
    const routeGrpNm = segments[1] ?? "";
    const routeNm = segments[2] ?? "";

    let group = groups.find((g) => g.headerName === routeGrpNm);
    if (!group) {
      group = { headerName: routeGrpNm, marryChildren: true, children: [] };
      groups.push(group);
    }

    group.children.push({
      headerName: routeNm,
      field: label,
      width: 140,
      sortable: false,
      type: ["numberColumn", "rightAligned"],
      ...(valueCellStyle ? { cellStyle: valueCellStyle } : {}),
    });
  });

  return groups;
};

// old RouteOutGrid.jsx:43-60 — ATTAIN(목표 미달률) 행만 100 이상 파랑 / 그 외 숫자 빨강.
// old는 rowIndex(2)로 짚었지만 행 순서가 바뀌어도 안전하도록 measure 값으로 판정한다.
const attainCellStyle = (params) => {
  if (params.data?.measure !== "ATTAIN") {
    return null;
  }
  if (typeof params.value !== "number") {
    return null;
  }
  return { color: params.value >= 100 ? "blue" : "red" };
};

// old RouteWip.jsx:72-82 — 첫 행(Total) 배경 강조. old는 rowIndex(0)였지만 데이터 값으로 판정한다.
const wipRowStyle = (params) => (params.data?.PROCESS === "Total" ? { background: TOTAL_ROW_BACKGROUND } : undefined);

const WipEohOutDashboard = ({ t, viewName, title }) => {
  // 응답 data 맵 원본을 그대로 보관: routeOutput = {LABEL, DATA, CHART_DATA}, routeWip = {LABEL, DATA}.
  const [routeOutput, setRouteOutput] = useState(null);
  const [routeWip, setRouteWip] = useState(null);

  const [outGridApi, setOutGridApi] = useState(null);
  const [wipGridApi, setWipGridApi] = useState(null);

  // 응답 순서 뒤바뀜 방지 토큰(fp-frontend.md §0-8).
  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => {
    const settings = FpGridSetting({ title, viewName });
    return {
      ...settings,
      // 읽기 전용 대시보드(old setNoneEditableGrid 대응) — 저장/삭제/DataState 추적 없음.
      defaultColDef: { ...settings.defaultColDef, editable: false },
      // 동적 컬럼 field가 데이터 값이라 '.'이 섞여도 경로로 해석되지 않게 차단.
      suppressFieldDotNotation: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TODO(MIG-T2): [wipeohoutdashboard/WipEohOutDashboard] 수동 재조회(전역 새로고침 버튼) 미이관.
  //   old: globalButtons "search" → loadData() 재실행(old 전역 버튼 바)
  //   사유: new 대시보드 선례(DpDashboard/RpDashboard/IPDashboard/DpStatusDashboard/FillRateDashboard)에
  //        전역 버튼 바 구조가 없고 조회 트리거는 마운트/필터 변경뿐 — 이 화면은 필터가 없어 마운트 1회.
  // 제외(MIG): old globalButtons "settings"(PopGeneralConfig 팝업)는 이관 범위 제외 확정
  //   (fp-dashboard-screen-guide.md §0-8). old의 getRouteGrpOptions 조회는 화면에 렌더되지 않는
  //   죽은 코드라 이관하지 않는다(screen-inventory.md §5 화면 표).
  const loadData = () => {
    const requestToken = ++latestReqRef.current;
    // 백엔드(WipEohOutDashboardService)는 params를 실제로 쓰지 않으므로 최소 페이로드만 보낸다.
    // (과거 DashBoardParameter.get()의 plain ObjectMapper로 전 요청이 405로 죽던 이력은
    // backend-gaps.md #8 — 2026-08-11 백엔드 수정 완료.)
    const params = { viewId: viewName };

    fpDashboardService.getWipEohOutRouteOutput(params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setRouteOutput(response?.data?.data ?? null);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setRouteOutput(null);
        debug(e);
      });

    fpDashboardService.getWipEohOutRouteWip(params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setRouteWip(response?.data?.data ?? null);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setRouteWip(null);
        debug(e);
      });
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 그리드1(공정별 생산 현황): 행 = 측정치 4개(measure), 열 = LABEL 동적 그룹.
  // 컬럼이 응답에 따라 매번 달라지므로 onGridReady가 아니라 데이터 도착 시점에
  // GridUtils.setColumnDefs를 다시 태운다(t 변경 = 언어 전환 시에도 재번역).
  useEffect(() => {
    if (!outGridApi) return;

    const columnDefs = [
      {
        headerName: "PROCESS",
        field: "measure",
        width: 180,
        // old RouteOutGrid displayCallback — measure 코드(TARGET_DAILY_QTY 등)를 번역해 표시.
        valueFormatter: (params) => (params.value ? t(params.value) : ""),
      },
      ...buildRouteColumnGroups(routeOutput?.LABEL ?? [], attainCellStyle),
    ];

    GridUtils.setColumnDefs({ api: outGridApi, columnDefs, viewName, gridId: "routeOutGrid", initState: false });
    GridUtils.setRowData(outGridApi, routeOutput?.DATA ?? []);
  }, [outGridApi, routeOutput, t, viewName]);

  // 그리드2(고객별 공정 대기 현황): 행 = 고객(첫 행 Total), 열 = LABEL 동적 그룹.
  // old RouteWip funcSetGridData — 그룹 배열을 {PROCESS: 고객명, [순번_그룹_공정]: wip} 행으로 변환.
  const routeWipRows = useMemo(() => {
    return (routeWip?.DATA ?? []).map((group) => {
      const row = {};
      (group ?? []).forEach((cell) => {
        row.PROCESS = cell.customerNm;
        row[`${cell.idx}_${cell.routeGrpNm}_${cell.routeNm}`] = cell.wip;
      });
      return row;
    });
  }, [routeWip]);

  useEffect(() => {
    if (!wipGridApi) return;

    const columnDefs = [
      { headerName: "FP_CUSTOMER", field: "PROCESS", width: 180 },
      ...buildRouteColumnGroups(routeWip?.LABEL ?? []),
    ];

    GridUtils.setColumnDefs({ api: wipGridApi, columnDefs, viewName, gridId: "routeWipGrid", initState: false });
    GridUtils.setRowData(wipGridApi, routeWipRows);
  }, [wipGridApi, routeWip, routeWipRows, t, viewName]);

  // TODO(MIG-T2): [wipeohoutdashboard/WipEohOutDashboard] 차트 막대 값 라벨(datalabels) 미이관.
  //   old: FpBar.jsx plugins.datalabels.color '#ffffff' + wingui/src/index.js:36 전역 등록으로
  //        막대에 흰색 값 라벨 실표시(/verify-screen 감사 W-01에서 확인 — 이관 시 "무동작" 오판 정정)
  //   사유: new 대응 ChartComposed가 recharts LabelList 미지원 — 공용 컴포넌트(DP/RP/IP 공유)라
  //        이 트랙에서 수정하지 않음(2026-08-11 사용자 지시). 값은 툴팁으로 확인 가능.
  // 차트(공정별 투입 대기 현황): old FpBar — LABEL 순서 그대로, TTL(합계) 열만 제외.
  // old는 배열을 라벨 개수로 잘랐지만(TTL이 항상 마지막) 인덱스 매핑이 같은 결과로 더 견고하다.
  const chartRows = useMemo(() => {
    const labels = routeOutput?.LABEL ?? [];
    const chartOutput = routeOutput?.CHART_DATA?.chartOutput ?? [];
    const chartEoh = routeOutput?.CHART_DATA?.chartEoh ?? [];

    return labels
      .map((label, index) => ({ routeNm: String(label).split("_")[2] ?? "", index }))
      .filter(({ routeNm }) => !routeNm.includes("TTL"))
      .map(({ routeNm, index }) => ({ routeNm, prodQty: chartOutput[index], preInputQty: chartEoh[index] }));
  }, [routeOutput]);

  const chartSeries = useMemo(() => ([
    { id: "prodQty", field: "prodQty", text: t("PROD"), type: "bar", groupName: t("qty"), typeOptions: { fill: CHART_FILL_PROD, barSize: 20 } },
    { id: "preInputQty", field: "preInputQty", text: t("PRE_INPUT_STATUS"), type: "bar", groupName: t("qty"), typeOptions: { fill: CHART_FILL_PRE_INPUT, barSize: 20 } },
  ]), [t]);

  const cardHeader = (Icon, label) => (
    <Box sx={{ display: "flex", height: "45px", alignItems: "flex-end", justifyContent: "center" }}>
      <Icon fontSize={"medium"} sx={{ mb: 0.2 }} />
      <Typography className="chart-title" variant="h6" sx={{ pl: 1, textAlign: "center", fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );

  // old 레이아웃(전폭 3단, data-grid h 5.5:4.5:6)을 % 비율(34/28/38)로 환산 — 가이드 §0-3(B) 골격.
  return (
    <Box className={"grid-area"} sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 560 }}>
      <Card sx={{ width: "100%", height: "calc(34% - 8px)", mb: 1.5 }}>
        {cardHeader(TableChartIcon, t("WI_FP_ROUTEOUTGRID"))}
        <Box className="ag-theme-balham" width={"100%"} height={"calc(100% - 45px)"}>
          <AgGridReact {...defaultGridMemo} onGridReady={(params) => setOutGridApi(params.api)} />
        </Box>
      </Card>
      <Card sx={{ width: "100%", height: "calc(28% - 8px)", mb: 1.5 }}>
        {cardHeader(LeaderboardIcon, t("WI_FP_ROUTEOUTCHART"))}
        <Box width={"100%"} height={"calc(100% - 45px)"}>
          {chartRows.length > 0 ? (
            <ChartComposed xAxis={"routeNm"} data={chartRows} dataSeries={chartSeries} options={CHART_OPTIONS} tooltipAnimation={false} />
          ) : (
            <Typography variant="subtitle1" sx={{ p: 3, height: "80%", textAlign: "center", fontWeight: 500 }}>
              {t("noChartData")}
            </Typography>
          )}
        </Box>
      </Card>
      <Card sx={{ width: "100%", height: "calc(38% - 8px)" }}>
        {cardHeader(TableChartIcon, t("WI_FP_ROUTEWIP"))}
        <Box className="ag-theme-balham" width={"100%"} height={"calc(100% - 45px)"}>
          <AgGridReact {...defaultGridMemo} getRowStyle={wipRowStyle} onGridReady={(params) => setWipGridApi(params.api)} />
        </Box>
      </Card>
    </Box>
  );
};

export default withTranslation()(WipEohOutDashboard);
